import { useModalStatusMessage } from '@/hooks';
import useConfirmationModal from '@/hooks/useConfirmationModal';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { getTicketIdWithHash } from '@/utils/getTicketIdWithHash';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { DateTimePickerController } from '@components/controllers/DateTimePickerController';
import { TextFieldController } from '@components/controllers/TextFieldController';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ConfirmationModal from '@components/modal/ConfirmationModal';
import ModalActions from '@components/modal/ModalActions';
import ModalTitle from '@components/modal/ModalTitle';
import CLOSE_DGA_TICKET from '@features/dga/graphql/mutations/closeTicket';
import OPEN_DGA_TICKET from '@features/dga/graphql/mutations/openTicket';
import UPDATE_DGA_TICKET from '@features/dga/graphql/mutations/updateTicket';
import GET_DGA_TICKET_STATE from '@features/dga/graphql/queries/getDGATicketState';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, FormHelperText, Link, Skeleton, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import dayjs from 'dayjs';
import { FC, Suspense, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  buildUpdateTicketInput,
  isEmptyUpdateTicketInput,
  needsCodeChangeConfirmation,
  type UpdateTicketPayload,
} from '../buildUpdateTicketInput';
import {
  EditValidationSchema,
  ValidationSchema,
  type EditFormValues,
  type FormValues,
} from '../validation';

interface Props {
  selectableDeviceIds?: string[];
  initialDeviceId?: string;
  onClose: () => void;
}

/** El ticket vigente del device, tal como lo necesita el formulario de corrección. */
interface OpenTicket {
  /** El `ticket_code` de la plataforma DGA — nombre heredado del modelo viejo (AMARU-718 D-09). */
  id: string;
  /** Fecha de apertura, en epoch ms (scalar `Date`). */
  openedAt: number;
  description: string | null;
}

/** Payload de apertura: los nombres del form ya son los del wire, no hay mapeo intermedio. */
interface OpenTicketPayload {
  deviceId: string;
  code: string;
  openedAt: number;
  description?: string;
}

const getDefaultValues = (deviceId: string): FormValues => ({
  deviceId,
  code: '',
  openedAt: dayjs(),
  description: '',
});

/*
  El formato del código se anuncia siempre, no solo al fallar. El campo se venía usando como nota
  libre ("Ticket de soporte abierto [#721844]", "tiket #347277 47/07/2026") y ese texto se imprime
  literal en el PDF que recibe el cliente (AMARU-733), así que conviene evitar el error en vez de
  solo reportarlo. Va como FormHelperText aparte y no en el `helperText` del campo:
  `TextFieldController` usa ese slot para el mensaje de validación, y pisarlo lo ocultaría.
*/
const CodeHelperText: FC = () => (
  <FormHelperText>
    <Trans>
      Solo el número que devuelve la plataforma de la DGA: 4 a 12 dígitos. Si se pega con #, se
      quita automáticamente.
    </Trans>
  </FormHelperText>
);

/*
  El detalle es el campo que AMARU-736 vino a agregar: hasta ahora el contexto ("se cortó la señal
  del sensor de nivel") terminaba metido dentro del código del ticket, que se imprime literal en el
  PDF del cliente. Acá es texto libre, opcional, y no se muestra en el widget de tableros
  compartidos ni en las vistas de resumen: es contexto operativo interno.
*/
const DescriptionHelperText: FC = () => (
  <FormHelperText>
    <Trans>
      Opcional. Información adicional del ticket.
    </Trans>
  </FormHelperText>
);

/** El abridor del diálogo de confirmación compartido del modal. */
type OpenConfirmationModal = ReturnType<typeof useConfirmationModal>['openConfirmationModal'];

/*
  Cuerpo del diálogo de cierre. Las líneas van como bloques dentro de un `DialogContentText`, que ya
  es un `<p>`: un `Typography` acá anidaría párrafos. La tercera línea solo aparece si hay un
  borrador que perder — el cierre no arrastra los cambios del formulario (ver D-007).
*/
const CloseTicketConfirmationContent: FC<{ dirty: boolean }> = ({ dirty }) => (
  <>
    <Box component="span" display="block">
      <Trans>No se podrá reabrir ni corregir.</Trans>
    </Box>
    <Box component="span" display="block">
      <Trans>Puedes abrir uno nuevo para el dispositivo.</Trans>
    </Box>
    {dirty && (
      <Box component="span" display="block" mt={1}>
        <Trans>Se perderán los cambios sin guardar.</Trans>
      </Box>
    )}
  </>
);

interface OpenTicketFormProps {
  deviceId: string;
  openTicket: (input: OpenTicketPayload) => Promise<void>;
  loading: boolean;
  onClose: () => void;
}

const OpenTicketForm: FC<OpenTicketFormProps> = ({ deviceId, openTicket, loading, onClose }) => {
  const { _, i18n } = useLingui();

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: getDefaultValues(deviceId),
    resolver: zodResolver(ValidationSchema(i18n)),
  });

  const onSubmit = handleSubmit(async (data) => {
    await openTicket({
      deviceId: data.deviceId,
      code: data.code,
      openedAt: data.openedAt.valueOf(),
      description: data.description,
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <Box px={2} mb={2}>
        <TextFieldController
          control={control}
          name="code"
          label={_(msg`Id de ticket`)}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
        <CodeHelperText />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePickerController
            control={control}
            name="openedAt"
            label={_(msg`Fecha de ingreso en la DGA`)}
            sx={{ mt: 2 }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </LocalizationProvider>
        <FormHelperText>
          <Trans>
            Si aún no has creado el ticket en la DGA, puedes crearlo desde{' '}
            <Link href="https://snia.mop.gob.cl/ticket/mee">este enlace.</Link>
          </Trans>
        </FormHelperText>

        <TextFieldController
          control={control}
          name="description"
          label={_(msg`Detalle`)}
          slotProps={{
            textField: {
              fullWidth: true,
              multiline: true,
              rows: 3,
              sx: { mt: 2 },
            },
          }}
        />
        <DescriptionHelperText />
      </Box>
      <ModalActions
        onSubmit="submit"
        onClose={onClose}
        submitLabel={_(msg`Abrir ticket`)}
        submitLoading={loading}
        closeButton={{
          label: _(msg`Cancelar`),
        }}
      />
    </form>
  );
};

interface EditTicketFormProps {
  deviceId: string;
  ticket: OpenTicket;
  onClose: () => void;
  updateTicket: (input: UpdateTicketPayload) => Promise<void>;
  closeTicket: () => Promise<void>;
  openConfirmationModal: OpenConfirmationModal;
  updating: boolean;
  closing: boolean;
}

/**
 * Vista del ticket vigente. Hasta AMARU-736 era solo una confirmación de cierre con los datos en
 * read-only: si el código se había tipeado mal, la única salida era cerrar el ticket y abrir otro,
 * dejando una fila cerrada espuria en un registro regulatorio. Ahora los tres campos son editables.
 *
 * Guardar y cerrar son dos operaciones distintas y separadas a propósito: el botón de cierre no
 * arrastra los cambios pendientes del formulario, porque encadenar corrección + cierre no sería
 * atómico y dejaría al usuario sin saber cuál de las dos falló.
 *
 * Las dos acciones confirman, pero no bajo la misma regla (D-007): el cierre **siempre**, porque es
 * irreversible; el guardado **solo si cambió el código**, que es el único campo que sale del sistema.
 */
const EditTicketForm: FC<EditTicketFormProps> = ({
  deviceId,
  ticket,
  onClose,
  updateTicket,
  closeTicket,
  openConfirmationModal,
  updating,
  closing,
}) => {
  const { _, i18n } = useLingui();

  const {
    handleSubmit,
    control,
    formState: { isDirty, dirtyFields },
  } = useForm<EditFormValues>({
    defaultValues: {
      code: ticket.id,
      openedAt: dayjs(ticket.openedAt),
      description: ticket.description ?? '',
    },
    resolver: zodResolver(EditValidationSchema(i18n)),
  });

  // Se mandan **solo los campos que el usuario tocó**, no el formulario completo: el prefill es una
  // foto del ticket tomada al abrir el modal, así que mandar los tres devolvería a su valor viejo
  // cualquier campo que otro admin haya corregido mientras tanto (lost update). El backend distingue
  // clave ausente de clave presente justamente para esto — ver `buildUpdateTicketInput`.
  const onSubmit = handleSubmit(async (data) => {
    const input = buildUpdateTicketInput(data, dirtyFields, ticket.openedAt);

    // "Guardar cambios" está deshabilitado mientras el formulario esté limpio, así que acá casi
    // siempre hay algo. El caso que queda es el cambio que no cambia nada (tipear `#` delante de un
    // código ya normalizado, reelegir la misma fecha): no hay nada que guardar, y mandar un input
    // vacío devolvería DGA_TICKET_UPDATE_EMPTY, un error que no le dice nada al usuario.
    if (isEmptyUpdateTicketInput(input)) {
      onClose();
      return;
    }

    // La confirmación se interpone acá y no en el `onClick` del botón: es el único punto donde zod ya
    // validó y el diff ya está armado. Preguntar antes obligaría a confirmar para después descubrir
    // que el código es inválido, o a confirmar un cambio de código que en realidad no cambió nada.
    if (needsCodeChangeConfirmation(input)) {
      openConfirmationModal(
        () => {
          void updateTicket(input);
        },
        {
          title: _(msg`¿Cambiar el código del ticket?`),
          // Solo el diff: el usuario ya tiene el formulario a la vista, y el valor que importa —el
          // que va a quedar impreso en el PDF del cliente— es este.
          content: `${getTicketIdWithHash(ticket.id)}  →  ${getTicketIdWithHash(input.code)}`,
          submitButton: { label: _(msg`Confirmar`) },
        },
      );
      return;
    }

    await updateTicket(input);
  });

  // Cerrar es irreversible y no tiene camino de vuelta en la UI: confirma siempre, tenga o no
  // cambios pendientes el formulario. La línea del borrador se suma solo si hay algo que perder.
  const onCloseTicket = () => {
    const ticketCode = getTicketIdWithHash(ticket.id);

    openConfirmationModal(
      () => {
        void closeTicket();
      },
      {
        title: _(msg`¿Cerrar el ticket ${ticketCode}?`),
        content: <CloseTicketConfirmationContent dirty={isDirty} />,
        submitButton: { label: _(msg`Cerrar ticket`), color: 'error' },
      },
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <Box px={2} mb={2}>
        <Typography display="block" mb={2}>
          <Trans>
            El dispositivo{' '}
            <b>
              <DeviceNameSpan deviceId={deviceId} />
            </b>{' '}
            tiene un ticket abierto. Puedes corregir sus datos o cerrarlo.
          </Trans>
        </Typography>

        <TextFieldController
          control={control}
          name="code"
          label={_(msg`Id de ticket`)}
          slotProps={{
            textField: {
              fullWidth: true,
            },
          }}
        />
        <CodeHelperText />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePickerController
            control={control}
            name="openedAt"
            label={_(msg`Fecha de ingreso en la DGA`)}
            sx={{ mt: 2 }}
            slotProps={{
              textField: {
                fullWidth: true,
              },
            }}
          />
        </LocalizationProvider>

        <TextFieldController
          control={control}
          name="description"
          label={_(msg`Detalle`)}
          slotProps={{
            textField: {
              fullWidth: true,
              multiline: true,
              rows: 3,
              sx: { mt: 2 },
            },
          }}
        />
        <DescriptionHelperText />
      </Box>
      <ModalActions
        onSubmit="submit"
        onClose={onClose}
        // `dirtyForm` deshabilita "Guardar cambios" mientras el formulario esté igual al ticket
        // guardado: evita del lado del cliente el DGA_TICKET_UPDATE_EMPTY del backend.
        dirtyForm={isDirty}
        submitLabel={_(msg`Guardar cambios`)}
        submitLoading={updating}
        closeButton={{
          label: _(msg`Cancelar`),
        }}
        // El cierre va como acción secundaria y separada del submit: `type="button"` explícito para
        // que estando dentro del `<form>` no dispare el guardado.
        customButtons={[
          {
            label: _(msg`Cerrar ticket`),
            onClick: onCloseTicket,
            type: 'button',
            color: 'error',
            variant: 'outlined',
            loading: closing,
          },
        ]}
      />
    </form>
  );
};

const HasDgaTicketsWrapper: FC<{
  deviceId: string;
  children: (ticket: OpenTicket | null) => JSX.Element;
}> = ({ deviceId, children }) => {
  const { data } = useSuspenseQuery(GET_DGA_TICKET_STATE, {
    variables: {
      deviceId,
    },
  });
  const ticket = data?.device.dga?.ticket ?? null;
  return children(
    ticket
      ? {
          id: ticket.id,
          openedAt: ticket.timestamp,
          description: ticket.description ?? null,
        }
      : null,
  );
};

const Content: FC<Props> = (props) => {
  const { _ } = useLingui();
  const [deviceId, setDeviceId] = useState<string | null>(props.initialDeviceId ?? null);
  const deviceIds = props.selectableDeviceIds;
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const [openTicket, { loading: opening }] = useMutation(OPEN_DGA_TICKET);
  const [updateTicket, { loading: updating }] = useMutation(UPDATE_DGA_TICKET);
  const [closeTicket, { loading: closing }] = useMutation(CLOSE_DGA_TICKET);
  // Un único diálogo de confirmación para las dos acciones destructivas del formulario de
  // corrección; cada llamada le pasa título, cuerpo y botón. Vive acá y no dentro del `<form>` de
  // `EditTicketForm` para que sus botones no queden dentro del formulario y disparen el submit.
  const { confirmationModalProps, openConfirmationModal } = useConfirmationModal();

  // Se muestra el mensaje del backend en vez de un literal genérico (AMARU-718). La validación del
  // formulario atrapa el formato antes de mandar la mutación, pero hay errores que el cliente no
  // puede anticipar y que sí traen un mensaje útil: DGA_TICKET_ALREADY_OPEN nombra el device que ya
  // tiene ticket abierto, y DGA_TICKET_NOT_FOUND al corregir significa que el ticket se cerró desde
  // otra sesión mientras este modal estaba abierto. Con "Error al abrir ticket" el usuario no sabía
  // qué corregir.
  const showError = (message: string, fallback: string) =>
    openModalErrorMessage(message || fallback);

  return (
    <>
      <ConfirmationModal {...confirmationModalProps} />
      <ModalTitle onClose={props.onClose} title={<Trans>Configurar ticket DGA</Trans>} />
      <Box px={2} mt={1}>
        {deviceIds?.length && (
          <StandardAutocompleteIdsWithFallback
            dataType="devices"
            value={deviceId}
            onChange={(_, value) => setDeviceId(value)}
            sx={{ mb: 2 }}
            queryOptions={{
              devices: {
                ids: props.selectableDeviceIds,
              },
            }}
          />
        )}
      </Box>
      {deviceId ? (
        <Suspense fallback={<Skeleton width="100%" height={300} variant="rounded" />}>
          <HasDgaTicketsWrapper deviceId={deviceId}>
            {(ticket) =>
              ticket ? (
                <EditTicketForm
                  // Los dos formularios se montan con `defaultValues`, que react-hook-form lee una
                  // sola vez. El modal permite cambiar de device desde el autocomplete sin
                  // desmontarse, así que sin `key` el formulario seguiría mostrando —y mandando—
                  // los datos del device anterior.
                  key={`${deviceId}:${ticket.id}`}
                  deviceId={deviceId}
                  onClose={props.onClose}
                  ticket={ticket}
                  openConfirmationModal={openConfirmationModal}
                  updating={updating}
                  closing={closing}
                  updateTicket={async (input) => {
                    await updateTicket({
                      variables: {
                        deviceId,
                        input,
                      },
                      onCompleted() {
                        openModalSuccessMessage(_(msg`Se ha actualizado el ticket correctamente.`));
                        props.onClose();
                      },
                      onError(error) {
                        showError(error.message, _(msg`Error al actualizar el ticket`));
                      },
                    });
                  }}
                  closeTicket={async () => {
                    await closeTicket({
                      variables: {
                        deviceId,
                      },
                      onCompleted() {
                        openModalSuccessMessage(_(msg`Se ha cerrado el ticket correctamente.`));
                        props.onClose();
                      },
                      onError(error) {
                        showError(error.message, _(msg`Error al cerrar ticket`));
                      },
                    });
                  }}
                />
              ) : (
                <OpenTicketForm
                  key={deviceId}
                  onClose={props.onClose}
                  deviceId={deviceId}
                  loading={opening}
                  openTicket={async ({ deviceId: id, ...input }) => {
                    await openTicket({
                      variables: {
                        deviceId: id,
                        input,
                      },
                      onCompleted() {
                        openModalSuccessMessage(_(msg`Se ha abierto un nuevo ticket.`));
                        props.onClose();
                      },
                      onError(error) {
                        showError(error.message, _(msg`Error al abrir ticket`));
                      },
                    });
                  }}
                />
              )
            }
          </HasDgaTicketsWrapper>
        </Suspense>
      ) : (
        <ModalActions onClose={props.onClose} />
      )}
    </>
  );
};

export const DgaTicketModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('dgaTicketModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm' }}>
      {state.open && (
        <>
          <Content
            selectableDeviceIds={
              'selectableDeviceIds' in state ? state.selectableDeviceIds : undefined
            }
            initialDeviceId={state.initialDeviceId}
            onClose={onClose}
          />
        </>
      )}
    </GlobalModal>
  );
};
